"use client";

import styles from "./PriceDisplay.module.scss";
import Button from "@components/atoms/Button";
import assetLoader from "@utils/internalImageLoder";
import Image from "@components/atoms/Image";
import { formatDeliveryDate } from "@utils/formatDate";
import { useEffect, useRef, useState } from "react";
import CitySelectionModal from "@components/organisms/CitySelectionModal";
import getIndianCurrencyFormat from "@helpers/getIndianCurrencyFormat";
import { getLoginUrl, addCaptureIntentIfNeeded } from "@utils/getLoginUrl";
import RequestCallBackModal from "../RequestCallBackModal";
import WishlistButton from "@components/atoms/WishlistButton";
import WishlistToast from "@components/atoms/WishlistToast";
import { useHasBottomNav } from "@hooks/useHasBottomNav";
import { useWishlistToast } from "@hooks/useWishlistToast";
import { useShortlist } from "@hooks/useShortlist";
import { PAGE_TYPES, PageType } from "@constants/pageTypes";
import { useRouter, usePathname } from "next/navigation";
import {
  CALLBACK_BUYING_PERIOD,
  prepareLeadDataForCapture,
  submitPreparedLeadForCapture,
} from "@helpers/leadCapture";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import TrustMarkerBadge from "@components/atoms/TrustMarkerBadge";
import MMSeoContentBlock from "@components/organisms/MMSeoContentBlock";
import { SEO_MM_PAGE_CONTENT } from "@constants/seo-mm-page-content";
import { LoginSdkConfig } from '@enums/LoginSdkConfig';
import { handleLoginFlow } from "@utils/loginFlowHandler";
import { formatPrice } from "@utils/numberFormatter";
import { formatPriceToLakhs } from "@utils/numberFormatters";
import { getFirstSellableVariantSlug } from "@helpers/variantHelpers";
import { useAppDispatch } from "@lib/redux/hooks";
import { updateProfileAsync } from "@lib/redux/slices/profileSlice";
import SimpleLoader from "@components/atoms/SimpleLoader/SimpleLoader";
import Text from "@components/atoms/Text";
import { updateAppBar } from "@hooks/useWebviewCallback";
import { COMPLETE_TRANSPARENT_APPBAR_CONFIG } from "@utils/appbarConfig";
import { calculateEmi } from "@utils/emiCalculator";
import { useIntentRedirectModal } from "@hooks/useIntentRedirectModal";
import type { BuyingIntentData } from "@services/globalIntentService";
import { postRequestCallback } from "@services/requestCallbackService";

interface PriceDisplayProps {
  title?: string;
  minPrice: number;
  maxPrice: number;
  minAdPriceDetails: any;
  cityName: string;
  waitingPeriod: string;
  onLocationClick?: () => void;
  className?: string;
  isLoggedIn: boolean;
  experessDelivery: boolean;
  city?: any;
  minAckodriveSavings?: any;
  brandName?: string;
  modelName?: string;
  pageType?: PageType;
  slug?: string;
  isSellable: boolean;
  carAvailability?: string;
  trustMarkerBadge?: string | null;
  isInApp?: boolean;
  monthlyOffer?: string;
  modelDetails?: any;
  userId?: string;
  showSeoContentInCard?: boolean;
  buyingIntent?: BuyingIntentData | null;
  mmAppbarConfig?: any;
}

export default function PriceDisplay({
  title,
  minPrice,
  minAdPriceDetails,
  maxPrice,
  cityName,
  waitingPeriod,
  className,
  isLoggedIn,
  experessDelivery,
  city,
  minAckodriveSavings,
  brandName,
  modelName,
  slug,
  isSellable,
  carAvailability,
  pageType,
  trustMarkerBadge,
  isInApp,
  monthlyOffer,
  modelDetails,
  userId,
  showSeoContentInCard = true,
  buyingIntent,
  mmAppbarConfig
}: PriceDisplayProps) {
  const hasSeoContent = Boolean(slug && SEO_MM_PAGE_CONTENT[slug]);
  const hasBottomNav = useHasBottomNav();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const appCtaRef = useRef<HTMLDivElement>(null);
  const mmvDetails = { make: brandName, model: modelName };
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [isRequestCallBackModalOpen, setIsRequestCallBackModalOpen] =
    useState(false);
  const [isLoadingCallback, setIsLoadingCallback] = useState(false);
  const hasInitializedCallback = useRef(false);
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const { showLoader, hideLoader } = useGlobalLoader();
  const firstSellableVariantSlug = getFirstSellableVariantSlug(
    modelDetails,
    slug ?? ""
  );
  const firstSellableVariant = modelDetails?.allVariantSlugs?.uniqueSortedVariants?.find(
    (variant: any) => variant?.slug === firstSellableVariantSlug
  );
  const helpMeBuyRedirectUrl = `/help-me-buy/${firstSellableVariantSlug}`;
  const { intentModal, openIntentOrRedirect } = useIntentRedirectModal({
    buyingIntent,
    cityName,
    brandName,
    modelName,
    variantName: firstSellableVariant?.variantName || "",
    pageType,
    loginType: "Get quote",
    continueRedirect: (redirectUrl) => {
      showLoader("Loading...");
      router.push(redirectUrl);
    },
    disableClose: Boolean(isInApp),
  });

  const { toast, showToast, hideToast } = useWishlistToast();
  const { isCarShortlisted, toggleShortlistItem } = useShortlist();
  const showWishlistBtn = slug && brandName && modelName;
  const isWishlisted = slug ? isCarShortlisted(slug) : false;

  const handleWishlistClick = async () => {
    if (!slug || !brandName || !modelName) return;
    const car = { id: slug, make: brandName, name: modelName };
    const result = await toggleShortlistItem(car);
    if (result.success) {
      showToast(car, !isWishlisted);
    }
  };

  const leadCaptureHandler: (isCallback?: boolean) => Promise<boolean> = async (isCallback = false) => {
    const leadData = await prepareLeadDataForCapture({
      pageType,
      carAvailability,
      brandName,
      modelName,
      cityName,
      vehicleColor: "",
      loginTypeLabel: "Get quote",
      componentName: "PriceDisplay",
      flowTypeLabel: "New Car",
    });
    if (!leadData) {
      return false;
    }

  if (!leadData) {
    return false;
  }

  await submitPreparedLeadForCapture({
    pageType,
    carAvailability,
    brandName,
    modelName,
    cityName,
    vehicleColor: "",
    leadData,
    loginTypeLabel: isCallback ? "Request callback" : "Get quote",
    componentName: "PriceDisplay",
  });

  return true;
};

  const processCallbackLeadAndShowModal = async () => {

    if (isInApp) {
      sessionStorage.setItem("flowType", "New Car");
      updateAppBar(COMPLETE_TRANSPARENT_APPBAR_CONFIG);
      openIntentOrRedirect({
        redirectUrl: helpMeBuyRedirectUrl,
      });
      return;
    }

    if (isLoadingCallback || hasInitializedCallback.current) {
      return;
    }

    setIsLoadingCallback(true);
    hasInitializedCallback.current = true;

    const leadData = await prepareLeadDataForCapture({
      pageType,
      carAvailability,
      brandName,
      modelName,
      cityName,
      vehicleColor: "",
      loginTypeLabel: "Request callback",
      componentName: "PriceDisplay",
      buyingPeriod: CALLBACK_BUYING_PERIOD,
      flowTypeLabel: "New Car",
    });
    if (!leadData) {
      hasInitializedCallback.current = false;
      setIsLoadingCallback(false);
      return;
    }

    // Update user profile with buying_period
    const profileUpdatePayload = {
      qualification_update_data: {
        buying_period: CALLBACK_BUYING_PERIOD,
      },
    };

    try {
      const [leadResult, , callbackResult] = await Promise.allSettled([
        submitPreparedLeadForCapture({
          pageType,
          carAvailability,
          brandName,
          modelName,
          cityName,
          vehicleColor: "",
          leadData,
          buyingPeriod: CALLBACK_BUYING_PERIOD,
          loginTypeLabel: "Request callback",
          componentName: "PriceDisplay",
        }),
        dispatch(
          updateProfileAsync({
            profileUpdates: { buying_period: CALLBACK_BUYING_PERIOD },
            apiPayload: profileUpdatePayload,
          })
        ),
        postRequestCallback({
          callback_message: `Request callback for ${brandName} ${modelName} in ${cityName}`,
        }),
      ]);

      const callbackLeadCaptured =
        leadResult.status === "fulfilled" &&
        leadResult.value?.success;
      const callbackSucceeded =
        callbackResult.status === "fulfilled" &&
        callbackResult.value?.success;

      if (
        callbackSucceeded
      ) {
        setIsRequestCallBackModalOpen(true);
      } else {
        hasInitializedCallback.current = false;
      }

      if (!callbackLeadCaptured) {
        console.error("Lead capture failed for callback request.");
      }

      if (callbackResult.status === "fulfilled") {
        const callbackResponse = callbackResult.value;
        if (!callbackResponse.success) {
          console.error("Request callback failed:", callbackResponse.error);
        }
      } else {
        console.error("Callback API call failed:", callbackResult.reason);
      }
    } finally {
      setIsLoadingCallback(false);
    }
  };

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setShowStickyCTA(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    const target = isInApp ? appCtaRef.current : ctaRef.current;
    if (target) {
      observer.observe(target);
    }
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [isInApp]);

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  const handleCallbackRequest = async () => {
    showLoader();
    updateAppBar(COMPLETE_TRANSPARENT_APPBAR_CONFIG);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_help",
        vehicle_make: brandName,
        vehicle_model: modelName,
        city_name: cityName,
        section: "MM page",
      },
    });
    try {
      const [,callbackResult] = await Promise.allSettled([
        leadCaptureHandler(true),
        postRequestCallback({
          callback_message: `Request callback for ${brandName} ${modelName} in ${cityName}`,
        }),
      ]);

      if (
        callbackResult.status === "fulfilled" &&
        callbackResult.value?.success
      ) {
        const redirectUrl =
          typeof window !== "undefined"
            ? `${window.location.pathname}${window.location.search}${window.location.hash}`
            : pathname;
        router.push(
          `/callback-confirmation?redirectUrl=${encodeURIComponent(redirectUrl)}`
        );
      }
    } catch (error) {
      updateAppBar(mmAppbarConfig);
      hideLoader();
      console.error("Error requesting callback:", error);
    }
  };

  if (isInApp) {
    const minPriceLakhs = parseFloat(formatPriceToLakhs(minPrice));
    const maxPriceLakhs = parseFloat(formatPriceToLakhs(maxPrice));

    return (
      <>
        <div className={`${styles.container} ${styles.appContainer} ${className || ""}`}>
          <div className={styles.appPriceRow}>
            <Text className={styles.appPriceRange}>
              ₹{minPriceLakhs} – ₹{maxPriceLakhs} lakh on-road
            </Text>
            {minAckodriveSavings > 0 && (
              <Text className={styles.appSavings}>
                Save up to ₹{getIndianCurrencyFormat(minAdPriceDetails?.ackodriveSavings)} <span>with ACKO Drive</span>
              </Text>
            )}
            {monthlyOffer && (
              <Text className={styles.appEmi}>
                EMI from ₹{calculateEmi(minPrice)?.toLocaleString('en-IN')}
              </Text>
            )}
            <div ref={appCtaRef} className={styles.appInlineCtaRow}>
              <Button
                size="ds"
                variant="outline"
                className={styles.appInlineTalkToUsCTA}
                onButtonClick={() => {
                  handleCallbackRequest();
                }}
                segmentEventProps={{
                  eventName: "action",
                  pageType: pageType,
                  eventProperties: {
                    action_type: "tap_btn",
                    action_name: "tap_btn_help",
                    vehicle_make: brandName,
                    vehicle_model: modelName,
                    city_name: cityName,
                    car_availability: carAvailability,
                    button_text: "Talk to us",
                    button_location: "top_fold",
                    section: "Top fold",
                  },
                }}
              >
                Talk to us
              </Button>
              <Button
                size="ds"
                className={styles.appInlineGetQuoteCTA}
                onButtonClick={() => {
                  processCallbackLeadAndShowModal();
                }}
                segmentEventProps={{
                  eventName: "action",
                  pageType: pageType,
                  eventProperties: {
                    action_type: "tap_btn",
                    action_name: "tap_btn_get_detailed_quote",
                    vehicle_make: brandName,
                    vehicle_model: modelName,
                    city_name: cityName,
                    car_availability: carAvailability,
                    button_text: "Get quote",
                    button_location: "top_fold",
                    section: "Top fold",
                  },
                }}
              >
                Get quote
              </Button>
            </div>
          </div>
        </div>
        {showStickyCTA && (
          <div className={styles.stickyDualCTAforApp}>
            <Button
              size="ds"
              variant="outline"
              className={styles.appFixedTalkToUsCTA}
              onButtonClick={() => {
                handleCallbackRequest();
              }}
              segmentEventProps={{
                eventName: "action",
                pageType: pageType,
                eventProperties: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_help",
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  city_name: cityName,
                  car_availability: carAvailability,
                  button_text: "Talk to us",
                  button_location: "sticky_cta",
                  section: "Sticky CTA",
                },
              }}
            >
              Talk to us
            </Button>
            <Button
              size="ds"
              className={styles.appFixedGetQuoteCTA}
              onButtonClick={() => {
                processCallbackLeadAndShowModal();
              }}
              segmentEventProps={{
                eventName: "action",
                pageType: pageType,
                eventProperties: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_get_detailed_quote",
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  city_name: cityName,
                  car_availability: carAvailability,
                  button_text: "Get quote",
                  button_location: "sticky_cta",
                  section: "Sticky CTA",
                },
              }}
            >
              Get quote
            </Button>
          </div>
        )}

        <WishlistToast
          isVisible={toast.show}
          isAdding={toast.isAdding}
          carMake={toast.carMake}
          carName={toast.carName}
          hasBottomNav={hasBottomNav}
          onViewWishlist={() => {
            hideToast();
            router.push("/wishlist");
          }}
          onUndo={async () => {
            if (!slug) return hideToast();
            const car = { id: slug, make: brandName!, name: modelName! };
            await toggleShortlistItem(car);
            hideToast();
          }}
          isInApp={isInApp}
        />

        {isRequestCallBackModalOpen && (
          <RequestCallBackModal
            isOpen={isRequestCallBackModalOpen}
            onClose={() => {
              setIsRequestCallBackModalOpen(false);
              hasInitializedCallback.current = false;
            }}
          />
        )}
        {intentModal}
        {isLoadingCallback && (
          <SimpleLoader
            isLoading={isLoadingCallback}
            variant="spinner"
            size="medium"
            overlay={true}
            text="Processing your request..."
          />
        )}
      </>
    );
  }

  const ACKODRIVE_OMS_QUOTE = "ackodrive20_oms";
  return (
    <div className={`${styles.wrapper} ${className || ""}`}>
      {isModalOpen && (
        <CitySelectionModal
          selectedCity={city}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      )}
      {showSeoContentInCard && (
        <div className={`${styles.topInfo} ${hasSeoContent ? styles.topInfoWithSeoContent : ""}`}>
          {(trustMarkerBadge || showWishlistBtn || title) && (
            <div className={styles.topInfoHeader}>
              <div className={styles.topInfoPrimary}>
                {trustMarkerBadge ? (
                  <div className={styles.topInfoBadge}>
                    <TrustMarkerBadge label={trustMarkerBadge} />
                  </div>
                ) : (
                  title && <h1 className={styles.title}>{title}</h1>
                )}
              </div>
              {showWishlistBtn && (
                <WishlistButton
                  isLoggedIn={isLoggedIn}
                  isInWishlist={isWishlisted}
                  onWishlistClick={handleWishlistClick}
                  size="small"
                  className={styles.topWishlistButton}
                  model_name={modelName}
                  brand_name={brandName}
                />
              )}
            </div>
          )}

          {trustMarkerBadge && title && <h1 className={styles.title}>{title}</h1>}      
          <MMSeoContentBlock slug={slug} className={styles.seoContentRow} isInApp={isInApp}/>
        </div>
      )}

      <div
        className={`${styles.container} ${hasSeoContent ? styles.containerWithSeoContent : ""}`}
      >
        <div
          className={`${styles.priceRow} ${hasSeoContent ? styles.priceRowWithSeoContent : ""}`}
        >
          {!showSeoContentInCard && trustMarkerBadge && (
            <div className={styles.trustMarkerRow}>
              <TrustMarkerBadge label={trustMarkerBadge} />
            </div>
          )}

          <div
            className={styles.locationInfo}
            onClick={() => setIsModalOpen(true)}
          >
            {(!isLoggedIn || !isSellable) && (
              <span className={styles.desktopPrice}>
                {`${formatPrice(minPrice)} — ${formatPrice(
                  maxPrice
                )}`}
              </span>
            )}
            <span>
              {isSellable && isLoggedIn
                ? `ACKO Drive price starts from`
                : `On-road price in ${cityName}`}
            </span>
            {(!isLoggedIn || !isSellable) && (
              <Button
                size="ds"
                onButtonClick={() => setIsModalOpen(true)}
                segmentEventProps={{
                  eventName: "action",
                  pageType: pageType,
                  eventProperties: {
                    action_type: "tap_btn",
                    action_name: "tap_btn_cities",
                    section: "Top fold",
                    car_availability: carAvailability,
                    city_name: cityName,
                    vehicle_make: brandName,
                    vehicle_model: modelName,
                  },
                }}
              >
                <Image
                  src={`${assetLoader()}/icons/expertReview/Chevron_down.svg`}
                  alt={"Right Arrow"}
                  className={styles.chevronDown}
                  width={20}
                  height={20}
                  priority={true}
                />
              </Button>
            )}
          </div>

          {isSellable && isLoggedIn ? (
            <span className={styles.LoggedInprice}>
              <div className={styles.minAdPrice}>
                {`${formatPrice(minAdPriceDetails?.ackodrivePrice || 0)}`}
              </div>
              <div className={styles.minPrice}>
                {`${formatPrice(minAdPriceDetails?.onroadPrice || 0)}`}
              </div>
              <div className={styles.minSaving}>
                {`Save ₹${getIndianCurrencyFormat(
                  minAdPriceDetails?.ackodriveSavings || 0
                )}`}
              </div>
            </span>
          ) : (
            <span className={styles.mobilePrice}>
              {`${formatPrice(minPrice)} — ${formatPrice(
                maxPrice
              )}`}
            </span>
          )}
          {isSellable &&
            (isLoggedIn ? (
              <div className={styles.horizontalLine} />
            ) : (
              <span className={styles.minimumSaving}>
                {`Get minimum savings of ${getIndianCurrencyFormat(
                  minAckodriveSavings
                )} on ACKO Drive`}
              </span>
            ))}
          {isSellable && (
            <div
              className={`${styles.CTADeliveryWrapper} ${
                !isLoggedIn && styles.logoutCTADeliveryWrapper
              }`}
            >
              <Button
                ref={ctaRef}
                size="ds"
                className={styles.exclusivePriceCTA}
                segmentEventProps={{
                  eventName: "action",
                  pageType: pageType,
                  eventProperties: {
                    action_type: "tap_btn",
                    action_name: isLoggedIn
                      ? "tap_btn_get_detailed_quote"
                      : "tap_btn_login_to_save_mm",
                    vehicle_make: brandName,
                    vehicle_model: modelName,
                    city_name: cityName,
                    car_availability: carAvailability,
                    section: "Top fold",
                  },
                }}
                onButtonClick={async () => {
                  if (isLoggedIn) {
                    showLoader("Processing your request...");
                    const canContinue = await leadCaptureHandler();
                    if (!canContinue) {
                      hideLoader();
                      return;
                    }
                    router.push(`/variant-selection/${slug}`);
                  } else {
                    showLoader("Redirecting to login...");
                    const currentUrl = addCaptureIntentIfNeeded(
                      window.location.href,
                      mmvDetails,
                      "Reveal price",
                      pageType
                    );
                    hideLoader();
                    sessionStorage.setItem("flowType", "New Car");
                    await handleLoginFlow(getLoginUrl(currentUrl), LoginSdkConfig.AD_PRICE);
                  }
                }}
              >
                {isLoggedIn ? `Get a quote` : `Unlock exclusive price`}
              </Button>

              {experessDelivery && (
                <div className={styles.expressDeliveryWrapper}>
                  <Image
                    src={`${assetLoader()}/icons/HeroSection/ExpressDeliveryPurple.svg`}
                    alt={"Right Arrow"}
                    width={14}
                    height={14}
                    priority={true}
                  />
                  {isLoggedIn ? (
                    <>
                      Express delivery by{" "}
                      <span className={styles.deliveryDate}>
                        {formatDeliveryDate(parseInt(waitingPeriod || "90"))}
                      </span>
                    </>
                  ) : (
                    `Express delivery available on select variants`
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isSellable && showStickyCTA && (
        <div className={styles.stickyCTAWrapper}>
          {!isLoggedIn && (
            <div className={styles.stickyInfo}>
              Unlock ACKO Drive’s exclusive pricing, offers, and more...
            </div>
          )}
          {isLoggedIn ? (
            <div className={styles.stickyDualCTA}>
              <Button
                size="ds"
                variant="outline"
                className={styles.stickyTalkToUsCTA}
                onButtonClick={() => {
                  processCallbackLeadAndShowModal();
                }}
                segmentEventProps={{
                  eventName: "action",
                  pageType: pageType,
                  eventProperties: {
                    action_type: "tap_btn",
                    action_name: "tap_btn_help",
                    vehicle_make: brandName,
                    vehicle_model: modelName,
                    city_name: cityName,
                    car_availability: carAvailability,
                    button_text: "Talk to us",
                    button_location: "sticky_cta",
                  },
                }}
              >
                Talk to us
              </Button>
              <Button
                size="ds"
                className={styles.stickyExclusivePriceCTA}
                onButtonClick={async () => {
                  showLoader("Processing your request...");
                  router.push(`/variant-selection/${slug}`);
                }}
                segmentEventProps={{
                  eventName: "action",
                  pageType: pageType,
                  eventProperties: {
                    action_type: "tap_btn",
                    action_name: "tap_btn_get_detailed_quote",
                    vehicle_make: brandName,
                    vehicle_model: modelName,
                    city_name: cityName,
                    car_availability: carAvailability,
                    button_text: "Get a quote",
                    button_location: "sticky_cta",
                  },
                }}
              >
                Get a quote
              </Button>
            </div>
          ) : (
            <Button
              size="ds"
              className={styles.stickyExclusivePriceCTALoggedOut}
              onButtonClick={async () => {
                showLoader("Redirecting to login...");
                if (typeof window !== "undefined") {
                  sessionStorage.setItem("loginType", "Reveal price");
                  sessionStorage.setItem("flowType", "New Car");
                  localStorage.setItem(
                    "MMV",
                    JSON.stringify({
                      make: mmvDetails?.make || "",
                        model: mmvDetails?.model || "",
                        variant: "",
                        color: "",
                      })
                    );
                  }
                  const loginUrl = getLoginUrl(
                    addCaptureIntentIfNeeded(
                      window.location.href,
                      mmvDetails,
                      "Reveal price",
                      pageType
                    )
                  );
                  hideLoader();
                  await handleLoginFlow(loginUrl, LoginSdkConfig.EXCLUSIVE_OFFER);
                }}
                segmentEventProps={{
                  eventName: "action",
                  pageType: pageType,
                  eventProperties: {
                    action_type: "tap_btn",
                    action_name: "tap_btn_login_to_save_mm",
                    vehicle_make: brandName,
                    vehicle_model: modelName,
                    city_name: cityName,
                    car_availability: carAvailability,
                    section: "Sticky CTA",
                  },
                }}
              >
                Log in to view
              </Button>
            )}
          </div>
        )}

      {isRequestCallBackModalOpen && (
        <RequestCallBackModal
          isOpen={isRequestCallBackModalOpen}
          onClose={() => {
            setIsRequestCallBackModalOpen(false);
            hasInitializedCallback.current = false; // Reset for next time
          }}
        />
      )}
      {isLoadingCallback && (
        <SimpleLoader
          isLoading={isLoadingCallback}
          variant="spinner"
          size="medium"
          overlay={true}
          text="Processing your request..."
        />
      )}
      <WishlistToast
        isVisible={toast.show}
        isAdding={toast.isAdding}
        carMake={toast.carMake}
        carName={toast.carName}
        hasBottomNav={hasBottomNav}
        onViewWishlist={() => {
          hideToast();
          router.push("/wishlist");
        }}
        onUndo={async () => {
          if (!slug || !brandName || !modelName) return hideToast();
          const car = { id: slug, make: brandName, name: modelName };
          await toggleShortlistItem(car);
          hideToast();
        }}
      />
    </div>
  );
}
